import {
  AnalysisStateSchema,
  FILTER_OPERATOR_ARITY,
  parseContract,
  type AnalysisState,
} from '@aibi/contracts';
import { isTemporalDimension, type SemanticModelIndex } from '@aibi/semantic-model';
import { AppError, err, ok, type ErrorDetail, type Result } from '@aibi/shared';
import {
  planDiagnosticsToDetails,
  type AnalysisDiagnostic,
  type AnalysisDiagnosticCode,
} from './planning/diagnostics.js';

/**
 * Valida un Analysis State contra el Semantic Model al que referencia.
 * El Analysis State nunca modifica el modelo: solo lo consulta a traves del indice.
 *
 * @example
 * const result = validateAnalysisState(rawState, new SemanticModelIndex(model));
 */
export function validateAnalysisState(
  input: unknown,
  index: SemanticModelIndex,
): Result<AnalysisState, AppError> {
  const parsed = parseContract(AnalysisStateSchema, input, 'contrato Analysis State');
  if (!parsed.ok) return parsed;

  const issues = findAnalysisIssues(parsed.value, index);
  return issues.length === 0
    ? ok(parsed.value)
    : err(
        new AppError('VALIDATION_ERROR', 'El Analysis State no es coherente con el modelo.', {
          details: issues,
        }),
      );
}

export function findAnalysisIssues(state: AnalysisState, index: SemanticModelIndex): ErrorDetail[] {
  return planDiagnosticsToDetails(findAnalysisDiagnostics(state, index));
}

/** Igual que `findAnalysisIssues`, con un codigo estable de diagnostico por incidencia. */
export function findAnalysisDiagnostics(
  state: AnalysisState,
  index: SemanticModelIndex,
): AnalysisDiagnostic[] {
  const issues: AnalysisDiagnostic[] = [];
  const report = (
    path: string,
    message: string,
    code: AnalysisDiagnosticCode = 'invalid-state',
  ): void => {
    issues.push({ severity: 'error', code, path, message });
  };
  const missing = (path: string, message: string): void => {
    report(path, message, 'missing-reference');
  };

  if (state.semanticModelId !== index.model.id) {
    report(
      'semanticModelId',
      `El analisis referencia "${state.semanticModelId}" pero el modelo es "${index.model.id}".`,
    );
  }

  const keys = new Set<string>();
  const dimensionKeys = new Set<string>();
  const measureKeys = new Set<string>();
  const registerKey = (key: string, path: string, bucket: Set<string>): void => {
    if (keys.has(key)) report(path, `Clave de campo duplicada "${key}".`);
    keys.add(key);
    bucket.add(key);
  };
  const checkTemporalDimension = (dimensionId: string, path: string, reason: string): boolean => {
    const resolved = index.resolveDimension(dimensionId);
    if (!resolved) {
      missing(path, `Dimension inexistente "${dimensionId}".`);
      return false;
    }
    if (!isTemporalDimension(resolved)) {
      report(path, `"${resolved.dimension.name}" ${reason}`, 'invalid-type');
    }
    return true;
  };

  state.dimensions.forEach((dimension, i) => {
    const path = `dimensions.${i}`;
    registerKey(dimension.key, `${path}.key`, dimensionKeys);
    const resolved = index.resolveDimension(dimension.dimensionId);
    if (!resolved) {
      missing(`${path}.dimensionId`, `Dimension inexistente "${dimension.dimensionId}".`);
    } else if (dimension.timeGrain !== undefined && !isTemporalDimension(resolved)) {
      report(
        `${path}.timeGrain`,
        `"${resolved.dimension.name}" no es una dimension temporal.`,
        'invalid-type',
      );
    }
  });

  state.measures.forEach((measure, i) => {
    const path = `measures.${i}`;
    registerKey(measure.key, `${path}.key`, measureKeys);
    if (!index.getMeasure(measure.measureId)) {
      missing(`${path}.measureId`, `Medida inexistente "${measure.measureId}".`);
    }
  });

  const filterIds = new Set<string>();
  state.filters.forEach((filter, i) => {
    const path = `filters.${i}`;
    if (filterIds.has(filter.id)) {
      report(`${path}.id`, `Id de filtro duplicado "${filter.id}".`, 'invalid-filter');
    }
    filterIds.add(filter.id);

    const { target } = filter;
    if (target.kind === 'dimension') {
      if (target.timeGrain === undefined) {
        if (!index.resolveDimension(target.dimensionId)) {
          missing(`${path}.target`, `Dimension inexistente "${target.dimensionId}".`);
        }
      } else {
        checkTemporalDimension(
          target.dimensionId,
          `${path}.target`,
          'no es una dimension temporal.',
        );
      }
    } else if (!index.getMeasure(target.measureId)) {
      missing(`${path}.target`, `Medida inexistente "${target.measureId}".`);
    }
    const [min, max] = FILTER_OPERATOR_ARITY[filter.operator];
    if (filter.values.length < min || filter.values.length > max) {
      report(
        `${path}.values`,
        `El operador "${filter.operator}" no admite ${filter.values.length} valores.`,
        'invalid-filter',
      );
    }
  });

  state.sorting.forEach((sort, i) => {
    if (!keys.has(sort.key)) missing(`sorting.${i}.key`, `Clave inexistente "${sort.key}".`);
  });

  state.grouping.rollupKeys.forEach((key, i) => {
    if (!dimensionKeys.has(key)) {
      missing(`grouping.rollupKeys.${i}`, `"${key}" no es una dimension del analisis.`);
    }
  });

  if (state.comparison) {
    const { comparison } = state;
    comparison.measureKeys.forEach((key, i) => {
      if (!measureKeys.has(key)) {
        missing(`comparison.measureKeys.${i}`, `"${key}" no es una medida del analisis.`);
      }
    });
    const dateDimensionId =
      comparison.dateDimensionId ?? index.model.settings.primaryDateDimensionId;
    if (dateDimensionId === undefined) {
      report(
        'comparison.dateDimensionId',
        'Indique la dimension de fecha o defina la fecha principal del modelo.',
      );
    } else {
      checkTemporalDimension(
        dateDimensionId,
        'comparison.dateDimensionId',
        'no es una dimension temporal.',
      );
    }
    if (comparison.kind === 'custom-offset' && !comparison.offset) {
      report('comparison.offset', 'Una comparacion "custom-offset" requiere offset.');
    }
  }

  state.selections.forEach((selection, i) => {
    if (!index.resolveDimension(selection.dimensionId)) {
      missing(`selections.${i}.dimensionId`, `Dimension inexistente "${selection.dimensionId}".`);
    }
  });

  state.drilldown.forEach((drill, i) => {
    const hierarchy = index.getHierarchy(drill.hierarchyId);
    if (!hierarchy) {
      missing(`drilldown.${i}.hierarchyId`, `Jerarquia inexistente "${drill.hierarchyId}".`);
      return;
    }
    const hasLevel = (levelId: string): boolean =>
      hierarchy.levels.some((level) => level.id === levelId);
    if (!hasLevel(drill.levelId)) {
      missing(`drilldown.${i}.levelId`, `Nivel inexistente "${drill.levelId}".`);
    }
    drill.path.forEach((step, j) => {
      if (!hasLevel(step.levelId)) {
        missing(`drilldown.${i}.path.${j}.levelId`, `Nivel inexistente "${step.levelId}".`);
      }
    });
  });

  for (const { key, path } of visualizationKeyReferences(state)) {
    if (!keys.has(key)) missing(path, `Clave inexistente "${key}".`);
  }

  return issues;
}

/** Todas las claves de campo que la visualizacion referencia, con su ruta. */
function visualizationKeyReferences(state: AnalysisState): { key: string; path: string }[] {
  const { visualization: viz } = state;
  const refs: { key: string; path: string }[] = [];
  const add = (key: string | undefined, path: string): void => {
    if (key !== undefined) refs.push({ key, path });
  };
  for (const [channel, channelKeys] of Object.entries(viz.encodings)) {
    channelKeys.forEach((key, i) => {
      add(key, `visualization.encodings.${channel}.${i}`);
    });
  }
  add(viz.grouping.seriesKey, 'visualization.grouping.seriesKey');
  viz.sorting.forEach((sort, i) => {
    add(sort.key, `visualization.sorting.${i}.key`);
  });
  for (const key of Object.keys(viz.formatting.fields)) {
    add(key, `visualization.formatting.fields.${key}`);
  }
  viz.formatting.conditional.forEach((rule, i) => {
    add(rule.key, `visualization.formatting.conditional.${i}.key`);
  });
  viz.tooltip.fields.forEach((key, i) => {
    add(key, `visualization.tooltip.fields.${i}`);
  });
  add(viz.map?.geoKey, 'visualization.map.geoKey');
  add(viz.map?.latitudeKey, 'visualization.map.latitudeKey');
  add(viz.map?.longitudeKey, 'visualization.map.longitudeKey');
  return refs;
}
